import React, { useEffect, useRef, useState } from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { cn } from '../../utils/cn';
import { Text } from '../base/Text';

export interface ToastAction {
	label: string;
	onClick: () => void;
}

interface Toast {
	id: string;
	message: string;
	type: 'success' | 'error' | 'warning' | 'info';
	duration?: number;
	/** Inline action button (e.g. "Undo", "Open"). Clicking it fires
	 * `action.onClick` and dismisses the toast. */
	action?: ToastAction;
	/** When set without `action`, the entire toast body is clickable.
	 * Typical use: "New message from X" → open the pop-out for that
	 * conversation. Click also dismisses the toast. */
	onClick?: () => void;
}

interface ToastActionOptions {
	duration?: number;
	onClick?: () => void;
	/** When provided, renders an inline action button with this label.
	 * When omitted, the whole toast body becomes clickable. */
	actionLabel?: string;
}

// Simple event emitter for toast notifications
class ToastEmitter {
	private listeners: Array<(toast: Toast) => void> = [];
	private idCounter = 0;

	subscribe(listener: (toast: Toast) => void) {
		this.listeners.push(listener);
		return () => {
			this.listeners = this.listeners.filter((l) => l !== listener);
		};
	}

	emit(toast: Toast) {
		this.listeners.forEach((listener) => listener(toast));
	}

	generateId(): string {
		this.idCounter += 1;
		return `${Date.now()}-${this.idCounter}`;
	}
}

const toastEmitter = new ToastEmitter();

// Export a simple API for showing toasts
export const toast = {
	success: (message: string, duration?: number, action?: ToastAction) => {
		toastEmitter.emit({
			id: toastEmitter.generateId(),
			message,
			type: 'success',
			duration,
			action,
		});
	},
	error: (message: string, duration?: number, action?: ToastAction) => {
		toastEmitter.emit({
			id: toastEmitter.generateId(),
			message,
			type: 'error',
			duration,
			action,
		});
	},
	warning: (message: string, duration?: number, action?: ToastAction) => {
		toastEmitter.emit({
			id: toastEmitter.generateId(),
			message,
			type: 'warning',
			duration,
			action,
		});
	},
	info: (message: string, duration?: number, action?: ToastAction) => {
		toastEmitter.emit({
			id: toastEmitter.generateId(),
			message,
			type: 'info',
			duration,
			action,
		});
	},
	/**
	 * Info-level toast with a click action. Use for notifications that
	 * open something when clicked (new chat message, share request,
	 * etc.). With `actionLabel`, renders an inline button; without it,
	 * the whole toast body is clickable. Either way, firing the action
	 * dismisses the toast.
	 */
	action: (message: string, options: ToastActionOptions): void => {
		const { duration, onClick, actionLabel } = options;
		toastEmitter.emit({
			id: toastEmitter.generateId(),
			message,
			type: 'info',
			duration,
			// When a label is provided, surface it as the inline action
			// button (same field licensing/undo toasts use). Without a
			// label, fall back to the body-clickable mode by setting
			// `onClick` directly on the toast.
			action:
				actionLabel != null && onClick != null
					? { label: actionLabel, onClick }
					: undefined,
			onClick: actionLabel == null ? onClick : undefined,
		});
	},
};

export const ToastContainer: React.FC = () => {
	const [toasts, setToasts] = useState<Toast[]>([]);
	const colors = useThemeColors();
	const containerRef = useRef<HTMLDivElement>(null);

	// Headless UI v2's useOutsideClick registers pointerdown/pointerup listeners on
	// `document` in the capture phase. Bubble-phase stopPropagation on the toast element
	// fires too late to prevent it. By registering on `window` (which is above `document`
	// in the capture chain), we intercept these events before Headless UI sees them.
	useEffect(() => {
		const handler = (e: Event) => {
			if (containerRef.current?.contains(e.target as Node)) {
				e.stopImmediatePropagation();
			}
		};

		window.addEventListener('pointerdown', handler, true);
		window.addEventListener('pointerup', handler, true);

		return () => {
			window.removeEventListener('pointerdown', handler, true);
			window.removeEventListener('pointerup', handler, true);
		};
	}, []);

	useEffect(() => {
		const unsubscribe = toastEmitter.subscribe((toast) => {
			setToasts((prev) => [...prev, toast]);

			// Auto remove after duration (default 5 seconds)
			const duration = toast.duration || 5000;
			setTimeout(() => {
				setToasts((prev) => prev.filter((t) => t.id !== toast.id));
			}, duration);
		});

		return unsubscribe;
	}, []);

	const removeToast = (id: string) => {
		setToasts((prev) => prev.filter((t) => t.id !== id));
	};

	// Using theme system colors for proper dark theme support
	const typeStyles = {
		success: cn(
			'dark:bg-surface bg-success-badge text-success-tint-fg border border-success-tint',
			'text-success',
			'border border-success-tint',
		),
		error: cn(
			'dark:bg-surface bg-danger-badge text-danger-tint-fg border border-danger-tint',
			'text-danger',
			'border border-danger-tint',
		),
		warning: cn(
			'dark:bg-surface bg-warning-badge text-warning-tint-fg border border-warning-tint',
			'text-warning',
			'border border-warning-tint',
		),
		info: cn(
			'dark:bg-surface bg-accent-badge text-accent-tint-fg border border-accent-tint',
			'text-accent',
			'border border-accent-tint',
		),
	};

	const icons = {
		success: (
			<svg
				className="w-5 h-5 text-success"
				fill="currentColor"
				viewBox="0 0 20 20"
			>
				<path
					fillRule="evenodd"
					d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
					clipRule="evenodd"
				/>
			</svg>
		),
		error: (
			<svg
				className="w-5 h-5 text-danger"
				fill="currentColor"
				viewBox="0 0 20 20"
			>
				<path
					fillRule="evenodd"
					d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
					clipRule="evenodd"
				/>
			</svg>
		),
		warning: (
			<svg
				className="w-5 h-5 text-warning"
				fill="currentColor"
				viewBox="0 0 20 20"
			>
				<path
					fillRule="evenodd"
					d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
					clipRule="evenodd"
				/>
			</svg>
		),
		info: (
			<svg
				className="w-5 h-5 text-accent"
				fill="currentColor"
				viewBox="0 0 20 20"
			>
				<path
					fillRule="evenodd"
					d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
					clipRule="evenodd"
				/>
			</svg>
		),
	};

	return (
		<div
			ref={containerRef}
			className="fixed top-4 right-4 z-[9999] pointer-events-none"
		>
			<div className="space-y-2">
				{toasts.map((toast) => {
					// Body-click mode: only when there's a body onClick AND no inline
					// action button. If both are set, the action button wins as the
					// primary CTA (avoids ambiguity over what a body click should do).
					const isBodyClickable = toast.onClick != null && toast.action == null;
					const fireBody = () => {
						toast.onClick?.();
						removeToast(toast.id);
					};
					return (
						<div
							key={toast.id}
							// Prevent mousedown from reaching the non-modal Sidebar's
							// document-level bubble-phase outside-click handler
							onMouseDown={(e) => e.stopPropagation()}
							onClick={(e) => {
								if (
									isBodyClickable &&
									!(e.target as HTMLElement).closest('button')
								) {
									fireBody();
								}
							}}
							className={cn(
								'pointer-events-auto flex items-center p-4 rounded-lg shadow-lg',
								'transform transition-all duration-300 ease-in-out',
								'translate-x-0 opacity-100',
								'dark:shadow-2xl dark:shadow-black/20',
								typeStyles[toast.type],
								isBodyClickable ? 'cursor-pointer hover:brightness-95' : '',
							)}
						>
							<div className="flex-shrink-0">{icons[toast.type]}</div>
							<div className="ml-3 flex-1 min-w-0">
								<Text className="text-sm font-medium whitespace-pre-line">
									{toast.message}
								</Text>
							</div>
							{toast.action != null && (
								<button
									onClick={(e) => {
										e.stopPropagation();
										toast.action?.onClick();
										removeToast(toast.id);
									}}
									className="ml-4 flex-shrink-0 inline-flex items-center px-3 py-1 text-sm font-semibold rounded-md hover:bg-black/10 dark:hover:bg-white/15 focus:outline-none transition-colors underline underline-offset-2"
								>
									{toast.action.label}
								</button>
							)}
							<button
								onClick={(e) => {
									e.stopPropagation();
									removeToast(toast.id);
								}}
								className="ml-2 flex-shrink-0 inline-flex rounded-md p-1 hover:bg-black/5 dark:hover:bg-white/10 focus:outline-none transition-colors"
								aria-label="Close"
							>
								<svg
									className="w-4 h-4"
									fill="currentColor"
									viewBox="0 0 20 20"
								>
									<path
										fillRule="evenodd"
										d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
										clipRule="evenodd"
									/>
								</svg>
							</button>
						</div>
					);
				})}
			</div>
		</div>
	);
};
